import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Sparkles, Zap, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";

const PRODUCTS = [
    { name: "Local SEO", color: "217 91% 60%" },
    { name: "SMS Avis", color: "330 81% 60%" },
    { name: "PleskDash", color: "199 89% 55%" },
];

export default function Hero() {
    return (
        <section className="relative isolate overflow-hidden pb-20 pt-32 sm:pt-40">
            {/* Aurora background */}
            <div className="pointer-events-none absolute inset-0 -z-10">
                <div className="absolute -top-40 left-1/2 h-[600px] w-[1200px] -translate-x-1/2 rounded-full bg-aurora animate-aurora-shift" />
                <div className="absolute inset-0 grid-pattern opacity-60" />
            </div>

            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <Badge
                            variant="outline"
                            className="gap-2 rounded-full border-border bg-card/60 px-4 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur"
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                            </span>
                            <Sparkles className="h-3.5 w-3.5 text-primary" />
                            Une suite SaaS pensée par et pour les agences web
                        </Badge>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-7 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-7xl"
                    >
                        Les outils <span className="text-gradient">secrets</span> des agences web<br className="hidden sm:block" /> enfin à votre portée.
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.25 }}
                        className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground sm:text-lg"
                    >
                        Des SaaS simples, performants et taillés pour le terrain. Gagnez des heures chaque semaine,
                        boostez le CA de vos clients et reprenez le contrôle de votre stack — sans jargon, sans fioritures.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.35 }}
                        className="mt-9 flex flex-col items-center gap-3 sm:flex-row"
                    >
                        <Button size="lg" className="group h-12 gap-2 rounded-xl bg-gradient-primary px-7 text-base font-semibold text-primary-foreground shadow-glow hover:opacity-95" asChild>
                            <a href="#solutions">
                                Découvrir la suite
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </a>
                        </Button>
                        <Button size="lg" variant="outline" className="h-12 gap-2 rounded-xl border-border bg-card/40 px-6 text-base font-medium text-foreground backdrop-blur hover:bg-card" asChild>
                            <a href="#histoire">L'histoire derrière Web Me Up</a>
                        </Button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground"
                    >
                        <div className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-success" />Testés en agence sur 20 ans</div>
                        <div className="inline-flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-primary" />Souscription en ligne</div>
                        <div className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-accent" />Sans engagement</div>
                    </motion.div>
                </div>

                {/* Hero visual: floating app cards */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.4 }}
                    className="relative mx-auto mt-20 max-w-6xl"
                >
                    <div className="relative">
                        {/* Glow halo */}
                        <div className="absolute inset-x-12 -top-10 h-40 rounded-full bg-gradient-primary opacity-30 blur-3xl" />

                        <div className="relative rounded-3xl border border-border bg-card/40 p-3 shadow-elegant backdrop-blur-xl">
                            <div className="flex items-center gap-1.5 px-3 py-2">
                                <span className="h-2.5 w-2.5 rounded-full bg-destructive/70" />
                                <span className="h-2.5 w-2.5 rounded-full bg-[hsl(40_90%_55%)]" />
                                <span className="h-2.5 w-2.5 rounded-full bg-success/70" />
                                <span className="ml-3 font-mono text-[10px] text-muted-foreground">app.webmeup.io / dashboard</span>
                            </div>
                            <div className="grid gap-3 rounded-2xl border border-border bg-background/60 p-3 sm:grid-cols-3">
                                {PRODUCTS.map((p, i) => (
                                    <DashboardTile key={p.name} title={p.name} accent={p.color} index={i} />
                                ))}
                            </div>
                        </div>

                        {/* Floating chips */}
                        <FloatingChip className="-left-4 top-12 sm:-left-8" delay="0s">
                            <span className="text-success">●</span> +38% d'avis Google
                        </FloatingChip>
                        <FloatingChip className="-right-3 top-24 sm:-right-10" delay="1.2s">
                            <Zap className="h-3 w-3 text-primary" /> 1 clic → 247 serveurs
                        </FloatingChip>
                        <FloatingChip className="-left-2 bottom-8 sm:-left-6" delay="0.6s">
                            <Sparkles className="h-3 w-3 text-accent" /> Fiche Google #1 local
                        </FloatingChip>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

const DashboardTile = ({ title, accent, index }) => (
    <div
        className="group relative overflow-hidden rounded-xl border border-border bg-gradient-card p-4 transition-all duration-500 hover:-translate-y-1 hover:border-primary/40"
        style={{ animationDelay: `${index * 120}ms` }}
    >
        <div
            className="absolute inset-x-0 top-0 h-px"
            style={{ background: `linear-gradient(90deg, transparent, hsl(${accent}), transparent)` }}
        />
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: `hsl(${accent})` }} />
                <span className="text-xs font-semibold text-foreground">{title}</span>
            </div>
            <span className="font-mono text-[10px] text-muted-foreground">live</span>
        </div>
        <div className="mt-4 space-y-2">
            <div className="h-1.5 w-3/5 rounded-full bg-muted">
                <div className="h-full rounded-full" style={{ width: '72%', background: `linear-gradient(90deg, hsl(${accent}), hsl(${accent} / 0.4))` }} />
            </div>
            <div className="h-1.5 w-4/5 rounded-full bg-muted">
                <div className="h-full rounded-full" style={{ width: '54%', background: `linear-gradient(90deg, hsl(${accent}), hsl(${accent} / 0.4))` }} />
            </div>
        </div>
        <div className="mt-4 flex items-end justify-between">
            <div>
                <div className="font-display text-2xl font-semibold text-foreground">
                    {["4.9★", "+247", "99.9%"][index]}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {["Note moyenne", "Avis ce mois", "Uptime serveurs"][index]}
                </div>
            </div>
            <Spark accent={accent} />
        </div>
    </div>
);

const Spark = ({ accent }) => (
    <svg viewBox="0 0 80 32" className="h-8 w-20">
        <defs>
            <linearGradient id={`g-${accent.replace(/\s/g, '')}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={`hsl(${accent})`} stopOpacity="0.6" />
                <stop offset="100%" stopColor={`hsl(${accent})`} stopOpacity="0" />
            </linearGradient>
        </defs>
        <path d="M0 24 L12 18 L24 22 L36 12 L48 16 L60 6 L72 10 L80 4" fill="none" stroke={`hsl(${accent})`} strokeWidth="1.5" />
        <path d="M0 24 L12 18 L24 22 L36 12 L48 16 L60 6 L72 10 L80 4 L80 32 L0 32 Z" fill={`url(#g-${accent.replace(/\s/g, '')})`} />
    </svg>
);

const FloatingChip = ({ children, className = "", delay = "0s" }) => (
    <div
        className={`absolute hidden items-center gap-1.5 rounded-full border border-border bg-card/90 px-3 py-1.5 text-xs font-medium text-foreground shadow-card-elevated backdrop-blur-md sm:inline-flex ${className} animate-float`}
        style={{ animationDelay: delay }}
    >
        {children}
    </div>
);
